import { Divider, Grid } from '@mui/material';
import { withStyles } from '@mui/styles';
import { escapeRegExp } from '@nx-smartmonkey/shared/helpers';
import {
  HSpacer,
  RoutalLoader,
  RoutalPalette,
  RoutalSearchInput,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  RoutalTooltip,
} from '@nx-smartmonkey/ui';
import _ from 'lodash';
import { useState } from 'react';

import ExportIcon from '../../../../assets/img/export.svg';
import More3Points from '../../../../assets/svg/More3Points';
import WarningIcon from '../../../../assets/svg/WarningIcon';
import { StopsCreateMenu } from '../../../../components/menus/StopsCreateMenu';
import { RouteAvatarName } from '../../../../components/RouteAvatarName';
import { sortDataByTableColumns, TableColumnsSortConfig } from '../../../../context/shared/tableColumns';
import { Driver } from '../../../../domain/driver/Driver';
import { Stop } from '../../../../domain/stop/Stop';
import { StopStatus } from '../../../../domain/stop/StopStatus';
import { saveData } from '../../../../helpers/excel/Common';
import flatten from '../../../../helpers/Flatten';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectGeoFences } from '../../../../hooks/project/useRetrieveActiveProjectGeoFences';
import { DetailTabTypes, useRetrieveRightPanelInfo } from '../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useRetrieveAllSupervisors } from '../../../../hooks/supervisor/useRetrieveAllSupervisors';
import { useRetrieveUserCredentials } from '../../../../hooks/user/useRetrieveUserCredentials';
import Button from '../../../../react-components/Button';
import FormattedMessage from '../../../../react-components/FormattedCustomMessage';
import { GeoFencesSelector } from '../../../../react-components/GeoFencesSelector';
import Text from '../../../../react-components/Text';
import StopCircleColor from '../../../shared/StopCircleColor';
import { StopRightPanel } from '../../shared/right-panel/stop-right-panel/StopRightPanel';
import VirtualizedDataTable from '../../shared/virtualized-datatable/VirtualizedDataTable';
import { StopsTableColumnKeys } from '../context/tableColumns/stopsTableColumns';
import { exportPlanStops } from '../excel-export/Stops';
import { useRetrieveActivePlan } from '../hooks/plan-details/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../hooks/plan-details/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from '../hooks/plan-details/useRetrieveActivePlanStops';
import { useSetActivePlanSelectedStops } from '../hooks/plan-details/useSetActivePlanSelectedStops';
import { useSetStopsColumns } from '../hooks/stop/useSetStopsColumns';
import { PlanStopsActionsMenu } from '../menus/PlanStopsActionsMenu';
import { getStopsColumns } from './PlanStopsListColumns';
import { PlanStopsListColumnsButton } from './PlanStopsListColumnsButton';

const stopStatus: StopStatus[] = [`pending`, `incomplete`, `completed`, `canceled`];

const UNASSIGNED_KEY = `unassigned`;

const styles = {
  divInside: {
    height: `100%`,
    width: `100%`,
  },
  divContainer: {
    padding: `0`,
    backgroundColor: `white`,
    marginBottom: 0,
    height: `100%`,
    width: `100%`,
  },
  grid: {
    width: `100%`,
    display: `flex`,
  },
};

type PlanStopsListProps = {
  classes: {
    divInside: string;
    divContainer: string;
    grid: string;
  };
};

export const PlanStopsList = withStyles(styles)(({ classes }: PlanStopsListProps) => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: plan, run: refetchPlan } = useRetrieveActivePlan();
  const { data: supervisors } = useRetrieveAllSupervisors();
  const { data: projectGeoFences } = useRetrieveActiveProjectGeoFences();
  const [geoFenceFilters, setGeoFenceFilters] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState<StopStatus[] | undefined>(undefined);
  const [filterDrivers, setFilterDrivers] = useState<string[] | undefined>(undefined);
  const [filterText, setFilterText] = useState<string>(``);
  const { detailType, setRightPanelInfo } = useRetrieveRightPanelInfo();
  const { selectedStops, setSelectedStops } = useSetActivePlanSelectedStops();

  const { data: geoFences } = useRetrieveActiveProjectGeoFences();
  const { data: stops, isLoading: isStopsLoading } = useRetrieveActivePlanStops();
  const { data: drivers } = useRetrieveActivePlanDrivers();
  const [contextAnchorTableStops, setContextAnchorTableStops] = useState<
    | {
        screenPosition: {
          top: number;
          left: number;
        };
        stops?: Stop[];
        stopClicked?: Stop;
      }
    | undefined
  >(undefined);
  const [sortStopsInfo, setSortStopsInfo] = useState<TableColumnsSortConfig<StopsTableColumnKeys>>({
    order: `desc`,
    orderBy: `created_at`,
  });

  const { tableColumns } = useSetStopsColumns();

  const filterStops = () => {
    let stopsFiltered: Stop[] = stops || [];

    if (stopsFiltered.length > 0) {
      if (geoFenceFilters.length > 0) {
        stopsFiltered = [
          ...stopsFiltered.filter((s: Stop) => {
            if (!s.location?.isValid()) return false;

            const isMarkerInsideGeoFence = geoFenceFilters.some((geoFenceId: string) => {
              const geoFence = (projectGeoFences ?? []).find((geoFence) => geoFence.id === geoFenceId);
              if (geoFence) {
                return geoFence.isMarkerInsideGeoFence({ lat: s.location!.lat!, lng: s.location!.lng! });
              }
              return false;
            });
            return isMarkerInsideGeoFence;
          }),
        ];
      }

      if ((filterStatus ?? []).length > 0) {
        stopsFiltered = [...stopsFiltered.filter((s: Stop) => s.status && filterStatus!.includes(s.status))];
      }

      if ((filterDrivers ?? []).length > 0) {
        stopsFiltered = [
          ...stopsFiltered.filter((s: Stop) => {
            if (s.driver_id) {
              return filterDrivers!.includes(s.driver_id);
            }
            return filterDrivers!.includes(UNASSIGNED_KEY);
          }),
        ];
      }

      if ((filterText ?? ``) !== ``) {
        const filterRegex = new RegExp(`${escapeRegExp(filterText).toLowerCase()}`, `ig`);
        stopsFiltered = [
          ...stopsFiltered.filter((s: Stop) => {
            const flattenStop = flatten({ target: s });
            const stopDriver = (drivers ?? []).find((r: Driver) => r.id === s.driver_id);
            return (
              Object.values(flattenStop).some((value) => `${value}`.match(filterRegex)) ||
              stopDriver?.label?.match(filterRegex)
            );
          }),
        ];
      }
    }

    return stopsFiltered;
  };

  const isFiltering = () => {
    return (
      geoFenceFilters.length > 0 ||
      (filterStatus ?? []).length > 0 ||
      (filterDrivers ?? []).length > 0 ||
      (filterText ?? ``) !== ``
    );
  };

  if (!activeProject) return null;

  const exportExcel = (all: boolean) => {
    const sortedStops = sortDataByTableColumns({
      data: all ? stops ?? [] : filterStops(),
      tableColumns,
      sortInfo: sortStopsInfo,
    });
    saveData(
      exportPlanStops({
        stops: selectedStops.length > 0 ? sortedStops.filter((stop) => selectedStops.includes(stop.id)) : sortedStops,
        project: activeProject!,
        tableColumns,
        planDrivers: drivers ?? [],
        supervisors: supervisors ?? [],
      }),
      `ExportStops_${plan?.label ?? ``}`
    );
  };

  const columns = getStopsColumns({
    activeProject,
    tableColumns,
    userCredentials,
    planDrivers: drivers,
    supervisors,
    onOpenRightPanel: (e, doc, detailTab?: DetailTabTypes) => {
      e.stopPropagation();
      setRightPanelInfo({
        detailType: `stop`,
        detailId: `${doc.id}`,
      });
    },
  });

  if (isStopsLoading) {
    return (
      <Grid container alignItems="center" justifyContent="center" style={{ height: `100%`, width: `100%` }}>
        <RoutalLoader width={60} />
      </Grid>
    );
  }

  return (
    <div className={classes.divInside} style={{ display: `flex` }}>
      <Grid container direction="column" wrap="nowrap" className={classes.divContainer}>
        <Grid container style={{ padding: `8px 22px`, backgroundColor: RoutalPalette.neutral00 }}>
          <Grid item container alignItems="flex-start" wrap="nowrap">
            <Grid container item>
              <RoutalSelect
                id="filter-stops-status"
                containerStyle={{
                  maxWidth: `220px`,
                  margin: `6px 0`,
                }}
                popoverProps={{
                  options: stopStatus.map(
                    (status: StopStatus): RoutalSelectMultiSelectOption => ({
                      key: status,
                      hideCheckIcon: false,
                      closeOnClick: false,
                      checked: filterStatus?.includes(status),
                      onClick: () => {
                        if (filterStatus?.includes(status)) {
                          const newFilterStatus = [...filterStatus.filter((st: StopStatus) => st !== status)];
                          setFilterStatus(newFilterStatus.length > 0 ? newFilterStatus : undefined);
                        } else {
                          setFilterStatus([...(filterStatus ?? []), status]);
                        }
                      },
                      content: (
                        <Text
                          style={{ cursor: `pointer` }}
                          intlId={`planner.plan_details.stops_list.header.filter.status.${status}`}
                        />
                      ),
                    })
                  ),
                }}
              >
                <Text
                  style={{
                    cursor: `pointer`,
                    width: `100%`,
                    textAlign: `justify`,
                    overflow: `hidden`,
                    textOverflow: `ellipsis`,
                    whiteSpace: `nowrap`,
                  }}
                >
                  {(filterStatus ?? stopStatus).reduce((amount, status, index) => {
                    return `${amount}${index > 0 ? `,` : ``} ${intl.formatMessage({
                      id: `planner.plan_details.stops_list.header.filter.status.${status}`,
                    })}`;
                  }, ``)}
                </Text>
              </RoutalSelect>

              {userCredentials?.subscription.test(`planner.geofences`) && activeProject.geo_fences_enabled ? (
                <>
                  <HSpacer medium />

                  <Grid item>
                    <GeoFencesSelector
                      geoFences={projectGeoFences ?? []}
                      selectedGeoFenceIds={geoFenceFilters}
                      selectButtonStyles={{
                        width: `240px`,
                      }}
                      noSelectedZonesText={
                        <Text
                          style={{ color: RoutalPalette.secondary40, cursor: `pointer` }}
                          intlId="planner.stops.no_zones_selected"
                        />
                      }
                      handleUpdate={(geoFences: string[]) => {
                        setGeoFenceFilters(geoFences);
                      }}
                    />
                  </Grid>
                </>
              ) : null}

              <HSpacer medium />

              <RoutalSelect
                id="filter-drivers"
                containerStyle={{
                  maxWidth: `220px`,
                  margin: `6px 0`,
                }}
                // style={{ borderColor: RoutalPalette.secondary00 }}
                popoverProps={{
                  options: [
                    ...(drivers ?? []).map((route: Driver): RoutalSelectMultiSelectOption => {
                      return {
                        key: route.id,
                        hideCheckIcon: false,
                        closeOnClick: false,
                        checked: filterDrivers?.includes(route.id),
                        onClick: () => {
                          if (filterDrivers?.includes(route.id)) {
                            const newFilterRoutes = [...filterDrivers.filter((rId: string) => rId !== route.id)];
                            setFilterDrivers(newFilterRoutes.length > 0 ? newFilterRoutes : undefined);
                          } else {
                            setFilterDrivers([...(filterDrivers ?? []), route.id]);
                          }
                        },
                        content: (
                          <Grid container alignItems="center" wrap="nowrap" gap="8px">
                            <Grid item>
                              <RouteAvatarName
                                name={route.getShortLabel()}
                                color={route.color?.value}
                                additionalStyles={{ width: `20px`, height: `20px` }}
                              />
                            </Grid>

                            <Grid item container style={{ width: `calc(100% - 24px - 8px)` }}>
                              <Text
                                tagName="span"
                                style={{
                                  textOverflow: `ellipsis`,
                                  whiteSpace: `nowrap`,
                                  overflow: `hidden`,
                                  cursor: `pointer`,
                                }}
                              >
                                {route.label ?? intl.formatMessage({ id: `no_name` })}
                              </Text>
                            </Grid>
                          </Grid>
                        ),
                      };
                    }),
                    {
                      key: UNASSIGNED_KEY,
                      hideCheckIcon: false,
                      closeOnClick: false,
                      checked: filterDrivers?.includes(UNASSIGNED_KEY),
                      onClick: () => {
                        if (filterDrivers?.includes(UNASSIGNED_KEY)) {
                          const newFilterRoutes = [...filterDrivers.filter((rId: string) => rId !== UNASSIGNED_KEY)];
                          setFilterDrivers(newFilterRoutes.length > 0 ? newFilterRoutes : undefined);
                        } else {
                          setFilterDrivers([...(filterDrivers ?? []), UNASSIGNED_KEY]);
                        }
                      },
                      content: (
                        <Grid container alignItems="center" wrap="nowrap" gap="8px">
                          <Grid item>
                            <WarningIcon color="primary" style={{ width: `26px`, height: `26px` }} />
                          </Grid>
                          <Grid item container style={{ width: `calc(100% - 26px - 8px)` }}>
                            <Text
                              style={{ cursor: `pointer` }}
                              intlId="planner.plan_details.stops_list.header.filter.routes.unassigned"
                            />
                          </Grid>
                        </Grid>
                      ),
                    },
                  ],
                }}
              >
                <Text
                  style={{
                    cursor: `pointer`,
                    width: `100%`,
                    textAlign: `justify`,
                    overflow: `hidden`,
                    textOverflow: `ellipsis`,
                    whiteSpace: `nowrap`,
                  }}
                >
                  {!filterDrivers ||
                  filterDrivers.length === 0 ||
                  filterDrivers.length === (drivers ?? []).length + 1 ? (
                    <FormattedMessage
                      tagName="span"
                      id="planner.plan_details.stops_list.header.filter.routes.all_selected"
                    />
                  ) : (
                    <>
                      {filterDrivers.length > 0 ? (
                        <FormattedMessage
                          tagName="span"
                          id="planner.plan_details.stops_list.header.filter.routes.num_selected"
                          values={{ num: filterDrivers.length }}
                        />
                      ) : (
                        <FormattedMessage
                          tagName="span"
                          id="planner.plan_details.stops_list.header.filter.routes.unassigned"
                        />
                      )}
                    </>
                  )}
                </Text>
              </RoutalSelect>
            </Grid>

            <Grid container item justifyContent="flex-end">
              {plan && (
                <>
                  <Grid item>
                    <Grid container direction="column" justifyContent="center" style={{ height: `100%` }}>
                      <RoutalSearchInput
                        inputContainerStyles={{
                          width: `260px`,
                        }}
                        onChange={_.debounce((text: any) => {
                          setFilterText(text);
                        }, 300)}
                        searchLive
                      />
                    </Grid>
                  </Grid>

                  <HSpacer medium />

                  <Grid item style={{ display: `flex`, height: `48px` }} alignItems="center">
                    <StopsCreateMenu
                      id="list-stops-create-actions"
                      intlId="planner.plan_details.stops_list.add_new"
                      style={{
                        backgroundColor: RoutalPalette.secondary40,
                        justifyContent: `space-between`,
                        borderRadius: `10px`,
                        padding: `0 14px`,
                      }}
                      hideSeparator
                      containerStyle={{
                        width: `unset`,
                      }}
                      planStops={stops ?? []}
                      activePlan={plan}
                      onStopsCreated={refetchPlan}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>

          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            wrap="nowrap"
            style={{
              minHeight: `46px`,
            }}
          >
            <Grid item container alignItems="center">
              {selectedStops.length === 0 ? (
                <Text
                  variant="body"
                  intlId={`planner.plan_details.stops_list.header.${isFiltering() ? `filtered_num_rows` : `num_rows`}`}
                  intlValues={{
                    num: filterStops().length,
                    total: (stops ?? []).length ?? 0,
                  }}
                />
              ) : (
                <Text
                  variant="body"
                  intlId="planner.plan_details.stops_list.header.selected_info"
                  intlValues={{
                    num: selectedStops.length,
                    total: (stops ?? []).length ?? 0,
                  }}
                />
              )}

              <HSpacer small />

              {selectedStops.length > 0 ? (
                <PlanStopsActionsMenu
                  type="popover"
                  stops={(stops ?? []).filter((stop) => selectedStops.includes(stop.id))}
                  showOpenCustomers={userCredentials?.subscription.test(`customers`)}
                  popoverProps={{
                    hideArrow: false,
                    style: {
                      justifyContent: `space-between`,
                    },
                    containerStyle: {
                      width: `240px`,
                    },
                    content: (
                      <Text
                        variant="body"
                        intlId="planner.plan_details.stops_list.header.actions"
                        style={{ cursor: `pointer` }}
                      />
                    ),
                  }}
                />
              ) : null}
            </Grid>

            <Grid item container alignItems="center" justifyContent="flex-end">
              <PlanStopsListColumnsButton id="plan-stops-configurable-table-columns" />
              <HSpacer small />
              <RoutalTooltip title={intl.formatMessage({ id: `planner.plan_details.stops_list.header.export_excel` })}>
                <Button
                  styleType="secondary"
                  onClick={() => exportExcel(false)}
                  style={{
                    height: `35px`,
                    width: `35px`,
                    padding: 0,
                    margin: 0,
                    borderRadius: `5px`,
                  }}
                >
                  <img alt="" height="20px" width="20px" src={ExportIcon} draggable="false" />
                </Button>
              </RoutalTooltip>
            </Grid>
          </Grid>
        </Grid>

        <Divider />

        <Grid container direction="column" style={{ height: `100%`, position: `relative` }}>
          {plan && (
            <Grid
              item
              container
              className={classes.grid}
              style={{
                overflowX: `auto`,
                overflowY: `hidden`,
                height: `100%`,
              }}
            >
              {(stops ?? []).length === 0 ? (
                <Grid container direction="column" alignItems="center" justifyContent="center" wrap="nowrap">
                  <Text intlId="planner.stops.no_stops.title" />
                </Grid>
              ) : (
                <Grid container style={{ width: `100%`, position: `relative` }}>
                  <div style={{ height: `100%`, width: `100%`, overflowX: `auto`, position: `absolute` }}>
                    {filterStops().length > 0 ? (
                      <VirtualizedDataTable
                        docs={sortDataByTableColumns({
                          data: filterStops(),
                          tableColumns,
                          sortInfo: sortStopsInfo,
                        }).map((stop: Stop) => {
                          const route = stop.driver_id
                            ? (drivers ?? []).find((planRoute: Driver) => planRoute.id === stop.driver_id)
                            : undefined;
                          const checkboxIcon = (
                            <StopCircleColor
                              isPickup={stop.type === `pickup`}
                              driver={route}
                              number={stop.order !== undefined ? stop.order + 1 : undefined}
                              planDrivers={drivers ?? []}
                              isSelected={selectedStops.includes(stop.id)}
                            />
                          );
                          return {
                            doc: stop,
                            checkboxIcon,
                          };
                        })}
                        showCheckboxes
                        onRowDoubleClick={(stop: Stop) => {
                          setRightPanelInfo({
                            detailType: `stop`,
                            detailId: `${stop.id}`,
                          });
                        }}
                        handleOnContextMenu={(e: any, stop: Stop) => {
                          e.preventDefault();
                          const found = (stops ?? []).find((stop) => selectedStops.includes(stop.id));
                          setContextAnchorTableStops({
                            screenPosition: {
                              top: e.clientY,
                              left: e.clientX,
                            },
                            stops: found ? (stops ?? []).filter((stop) => selectedStops.includes(stop.id)) : [stop],
                            stopClicked: found ?? stop,
                          });
                        }}
                        onMultipleSelection={(docs: Array<Stop>) => setSelectedStops(docs.map((doc) => doc.id))}
                        selectedDocs={(stops ?? []).filter((stop) => selectedStops.includes(stop.id))}
                        initialSortBy={sortStopsInfo.orderBy}
                        initialSortOrder={sortStopsInfo.order}
                        onColumnSort={(column: any, order: `asc` | `desc`) => {
                          setSortStopsInfo({ order, orderBy: column });
                        }}
                        columns={columns}
                      />
                    ) : (
                      <Grid
                        container
                        direction="column"
                        alignItems="center"
                        style={{
                          height: `100%`,
                          padding: `32px`,
                        }}
                      >
                        <Text variant="body-large" intlId="no_results" />
                      </Grid>
                    )}
                  </div>
                </Grid>
              )}
            </Grid>
          )}

          {contextAnchorTableStops && contextAnchorTableStops.stops && (
            <PlanStopsActionsMenu
              type="contextual"
              stops={
                selectedStops && selectedStops.length > 0
                  ? (stops ?? []).filter((stop) => selectedStops.includes(stop.id))
                  : contextAnchorTableStops.stops
              }
              contextAnchor={{ screenPosition: contextAnchorTableStops.screenPosition }}
              setContextAnchor={setContextAnchorTableStops}
              showOpenCustomers={userCredentials?.subscription.test(`customers`)}
            />
          )}
        </Grid>
      </Grid>
      {detailType === `stop` ? (
        <StopRightPanel
          selectedStopsIds={selectedStops}
          validatePlan
          plan={plan}
          planDrivers={drivers}
          planGeoFences={geoFences}
          stopActionsMenu={
            <PlanStopsActionsMenu
              type="popover"
              stops={[]}
              showOpenCustomers={userCredentials?.subscription.test(`customers`)}
              popoverProps={{
                hideArrow: true,
                content: <More3Points />,
                style: {
                  color: RoutalPalette.secondary60,
                  padding: 0,
                  margin: 0,
                  border: `unset`,
                  backgroundColor: RoutalPalette.neutral00,
                },
              }}
            />
          }
        />
      ) : null}
    </div>
  );
});
